import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Heart, Plus, Edit, Trash2, Camera, X, User } from 'lucide-react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const Pets = () => {
  const { currentUser } = useAuth();
  const [pets, setPets] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'dog',
    breed: '',
    age: '',
    weight: '',
    clientId: '',
    medical: '',
    feeding: '',
    behavioral: '',
    photoURL: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [showModal]);

  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      if (!currentUser) return;

      try {
        const clientsRef = collection(db, 'clients');
        const q = query(clientsRef, where('sitterId', '==', currentUser.uid));
        const snapshot = await getDocs(q);
        const clientsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClients(clientsData.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    fetchClients();
  }, [currentUser]);

  // Real-time listener for pets
  useEffect(() => {
    if (!currentUser) return;

    const petsRef = collection(db, 'pets');
    const q = query(petsRef, where('sitterId', '==', currentUser.uid));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const petsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPets(petsData.sort((a, b) => a.name.localeCompare(b.name)));
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'dog',
      breed: '',
      age: '',
      weight: '',
      clientId: '',
      medical: '',
      feeding: '',
      behavioral: '',
      photoURL: '',
    });
    setEditingPet(null);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return formData.photoURL;

    try {
      setUploadingPhoto(true);
      const fileName = `pet-photos/${currentUser.uid}/${Date.now()}_${photoFile.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, photoFile);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
      return formData.photoURL;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Upload photo if new one selected
      const photoURL = await uploadPhoto();

      const petData = {
        ...formData,
        photoURL,
        sitterId: currentUser.uid,
      };

      if (editingPet) {
        // Update existing pet
        const petRef = doc(db, 'pets', editingPet.id);
        await updateDoc(petRef, petData);
      } else {
        // Add new pet
        const petsRef = collection(db, 'pets');
        await addDoc(petsRef, {
          ...petData,
          createdAt: new Date().toISOString(),
        });
      }

      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving pet:', error);
      alert('Failed to save pet. Please try again.');
    }
  };

  const handleEdit = (pet) => {
    setEditingPet(pet);
    setFormData({
      name: pet.name,
      type: pet.type || 'dog',
      breed: pet.breed || '',
      age: pet.age || '',
      weight: pet.weight || '',
      clientId: pet.clientId || '',
      medical: pet.medical || '',
      feeding: pet.feeding || '',
      behavioral: pet.behavioral || '',
      photoURL: pet.photoURL || '',
    });
    if (pet.photoURL) {
      setPhotoPreview(pet.photoURL);
    }
    setShowModal(true);
  };

  const handleDelete = async (petId) => {
    if (!window.confirm('Are you sure you want to delete this pet? This action cannot be undone.')) {
      return;
    }

    try {
      const petRef = doc(db, 'pets', petId);
      await deleteDoc(petRef);
    } catch (error) {
      console.error('Error deleting pet:', error);
      alert('Failed to delete pet. Please try again.');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown';
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Heart className="h-8 w-8 text-pastel-pink" />
            <div>
              <h1 className="text-3xl font-bold text-brand-brown">Pets</h1>
              <p className="text-brand-brown/60">Manage your furry (and not so furry) friends</p>
            </div>
          </div>

          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add Pet</span>
          </button>
        </div>

        {/* Pets Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pastel-pink"></div>
          </div>
        ) : pets.length === 0 ? (
          <div className="card p-12 text-center">
            <Heart className="h-16 w-16 text-pastel-pink/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-brand-brown mb-2">No pets yet</h3>
            <p className="text-brand-brown/60 mb-6">
              Add your first pet profile to keep track of their care details!
            </p>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="btn-primary mx-auto"
            >
              <Plus className="h-5 w-5 inline mr-2" />
              Add Your First Pet
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pets.map((pet) => (
              <div key={pet.id} className="card overflow-hidden hover:shadow-xl transition-all duration-200">
                {/* Pet Photo - Much Larger! */}
                <div className="h-64 md:h-72 bg-gradient-to-br from-pastel-pink to-pastel-blue flex items-center justify-center relative">
                  {pet.photoURL ? (
                    <img 
                      src={pet.photoURL} 
                      alt={pet.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center">
                      <Heart className="h-24 w-24 text-white/50 mx-auto mb-2" />
                      <p className="text-white/70 text-sm">No photo yet</p>
                    </div>
                  )}
                  <div className="absolute top-3 right-3 flex space-x-2">
                    <button
                      onClick={() => handleEdit(pet)}
                      className="p-2.5 rounded-lg bg-white/90 hover:bg-white text-pastel-blue transition-colors shadow-lg"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(pet.id)}
                      className="p-2.5 rounded-lg bg-white/90 hover:bg-white text-red-500 transition-colors shadow-lg"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Pet Info */}
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-brand-brown mb-1">{pet.name}</h3>
                    <p className="text-sm text-brand-brown/60">
                      {pet.breed} • {pet.type.charAt(0).toUpperCase() + pet.type.slice(1)}
                    </p>
                  </div>

                  <div className="space-y-2 text-sm">
                    {pet.clientId && (
                      <div className="flex items-center space-x-2 text-brand-brown/70">
                        <User className="h-4 w-4 flex-shrink-0" />
                        <span>{getClientName(pet.clientId)}</span>
                      </div>
                    )}

                    {pet.age && (
                      <div className="flex justify-between">
                        <span className="text-brand-brown/60">Age:</span>
                        <span className="text-brand-brown font-medium">{pet.age} years</span>
                      </div>
                    )}

                    {pet.weight && (
                      <div className="flex justify-between">
                        <span className="text-brand-brown/60">Weight:</span>
                        <span className="text-brand-brown font-medium">{pet.weight} lbs</span>
                      </div>
                    )}

                    {pet.medical && (
                      <div className="mt-4 pt-4 border-t border-brand-brown/10">
                        <p className="text-brand-brown/60 text-xs">
                          <span className="font-semibold">Medical:</span> {pet.medical}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Pet Modal */}
        {showModal && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            style={{ overflow: 'hidden' }}
          >
            <div 
              className="bg-white rounded-2xl max-w-2xl w-full flex flex-col"
              style={{ maxHeight: '90vh' }}
            >
              <div className="flex-shrink-0 bg-white border-b border-brand-brown/10 p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-brand-brown">
                    {editingPet ? 'Edit Pet' : 'Add New Pet'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="p-2 rounded-xl hover:bg-pastel-pink/30 transition-colors"
                  >
                    <X className="h-5 w-5 text-brand-brown" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1 scrollbar-thin">
                {/* Photo Upload */}
                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-2">
                    Pet Photo
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="relative w-32 h-32 rounded-xl overflow-hidden bg-pastel-pink/20 flex items-center justify-center">
                      {photoPreview ? (
                        <img 
                          src={photoPreview} 
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Heart className="h-12 w-12 text-brand-brown/30" />
                      )}
                    </div>
                    <label className="btn-secondary cursor-pointer">
                      <Camera className="h-5 w-5 inline mr-2" />
                      Choose Photo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoSelect}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      Pet Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Buddy"
                      className="input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      Owner *
                    </label>
                    <select
                      required
                      value={formData.clientId}
                      onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                      className="input"
                    >
                      <option value="">Select owner</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>{client.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      Type *
                    </label>
                    <select
                      required
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="input"
                    >
                      <option value="dog">Dog</option>
                      <option value="cat">Cat</option>
                      <option value="bird">Bird</option>
                      <option value="rabbit">Rabbit</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      Breed
                    </label>
                    <input
                      type="text"
                      value={formData.breed}
                      onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
                      placeholder="Golden Retriever"
                      className="input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      Age (years)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      placeholder="3"
                      className="input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      Weight (lbs)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.weight}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      placeholder="65"
                      className="input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">
                    Medical Information
                  </label>
                  <textarea
                    rows="2"
                    value={formData.medical}
                    onChange={(e) => setFormData({ ...formData, medical: e.target.value })}
                    placeholder="Allergies, medications, vet info, etc."
                    className="textarea"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">
                    Feeding Schedule & Diet
                  </label>
                  <textarea
                    rows="2"
                    value={formData.feeding}
                    onChange={(e) => setFormData({ ...formData, feeding: e.target.value })}
                    placeholder="Times, amounts, food brand, treats, etc."
                    className="textarea"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">
                    Behavioral Notes
                  </label>
                  <textarea
                    rows="2"
                    value={formData.behavioral}
                    onChange={(e) => setFormData({ ...formData, behavioral: e.target.value })}
                    placeholder="Temperament, likes/dislikes, special needs, etc."
                    className="textarea"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 btn-secondary py-3"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingPhoto}
                    className="flex-1 btn-primary py-3 disabled:opacity-50"
                  >
                    {uploadingPhoto ? 'Uploading...' : editingPet ? 'Update Pet' : 'Add Pet'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Pets;
